import React, { useEffect, useState } from 'react';
import { Radar, Search, XCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ScanFormProps {
  domain: string;
  setDomain: (value: string) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  loading: boolean;
  error: string | null;
  target: number; // Valor final para el contador
  onCancel: () => void; // Callback para cancelar el escaneo
}

const ScanForm: React.FC<ScanFormProps> = ({
  domain,
  setDomain,
  onSubmit,
  loading,
  error,
  target,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const duration = 3000; // Duración de la animación en milisegundos

  useEffect(() => {
    let start: number | null = null;
    const easeOutQuad = (t: number) => t * (2 - t);
    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const progressNormalized = Math.min(progress / duration, 1);
      const easedProgress = easeOutQuad(progressNormalized);
      setCount(Math.floor(target * easedProgress));
      if (progress < duration) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [target, duration]);

  // Determinamos el texto y estilos del botón basado en el estado de carga y hover
  const buttonText = loading
    ? isHovered
      ? t('scan.cancel')
      : t('scan.scanning')
    : t('scan.analyzeButton');

  const buttonClasses = loading
    ? isHovered
      ? 'px-8 py-4 text-xl bg-red-600 rounded-lg flex items-center gap-2 transition-all text-white'
      : 'px-8 py-4 text-xl bg-gray-500 rounded-lg flex items-center gap-2 transition-all text-white'
    : 'px-8 py-4 text-xl bg-accent hover:bg-yellow-600 rounded-lg flex items-center gap-2 transition-colors text-white';
    

  return (
    <form onSubmit={onSubmit} className="w-full max-w-7xl mx-auto mb-12">
      {/* Contenedor para el contador y el input */}
      <div className="flex items-center gap-10 w-full">
        {/* Contador a la izquierda */}
        <div className="flex items-center gap-4 w-40">
          <Radar className="w-12 h-12 text-primary animate-pulse" />
          <div className="text-left">
          <h3 className="text-5xl font-bold text-white">
            {Number(count).toLocaleString('es-ES')}
          </h3>
            <p className="text-textLight text-lg">{t('scan.scansPerformed')}</p>
          </div>
        </div>

        {/* Contenedor para el input y el botón */}
        <div className="group relative overflow-hidden rounded-lg bg-card px-10 py-8 shadow-lg transition-all hover:shadow-xl border border-gray-700 hover:border-primary-500 flex-1">
          <div className="flex items-center gap-6">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder={t('scan.inputPlaceholder')}
              className="flex-1 px-6 py-4 text-xl rounded-lg bg-gray-600 border border-gray-600 focus:outline-none focus:border-blue-500 text-white"
              disabled={loading}
            />
            <button
              type={loading ? 'button' : 'submit'}
              className={buttonClasses}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => {
                if (loading) onCancel();
              }}
            >
              {loading ? (
                isHovered ? (
                  <>
                    <XCircle className="w-5 h-5" /> {buttonText}
                  </>
                ) : (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> {buttonText}
                  </>
                )
              ) : (
                <>
                  <Search className="w-5 h-5" /> {buttonText}
                </>
              )}
            </button>
          </div>
          {/* Sugerencias debajo del input */}
          <div className="mt-3 text-gray-400 text-sm">
            {t('scan.suggestions')}
          </div>
        </div>
      </div>
      {error && <div className="mt-4 text-red-400 text-center">{error}</div>}
    </form>
  );
};

export default ScanForm;
